import { Clock, CheckCircle2, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useProducerPlaceSuggestions } from "@/hooks/usePlaceSuggestions";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDistanceToNow } from "date-fns";
import { sv } from "date-fns/locale";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";

interface PlaceSuggestionsListProps {
  producerId: string;
}

export function PlaceSuggestionsList({ producerId }: PlaceSuggestionsListProps) {
  const { data: suggestions, isLoading } = useProducerPlaceSuggestions(producerId);

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (!suggestions || suggestions.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-4 text-center border rounded-md bg-muted/30">
        Du har inga platsförslag ännu.
      </div>
    );
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-warning" />;
      case 'approved':
        return <CheckCircle2 className="h-4 w-4 text-primary" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-destructive" />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'pending':
        return 'Väntar på granskning';
      case 'approved':
        return 'Godkänd';
      case 'rejected':
        return 'Avvisad';
      default:
        return status;
    }
  };

  const getStatusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case 'pending':
        return 'secondary';
      case 'approved':
        return 'default';
      case 'rejected':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium">Dina platsförslag</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {suggestions.slice(0, 5).map((suggestion) => (
          <div 
            key={suggestion.id} 
            className="flex items-center justify-between py-2 border-b last:border-0"
          >
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{suggestion.suggested_name}</p>
              <p className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(suggestion.created_at), { 
                  addSuffix: true, 
                  locale: sv 
                })}
              </p>
              {/* Show link to the created place if approved */}
              {suggestion.status === 'approved' && suggestion.approved_place_id && (
                <Link 
                  to={`/place/${suggestion.approved_place_id}`}
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1"
                >
                  Visa plats
                  <ExternalLink className="h-3 w-3" />
                </Link>
              )}
            </div>
            <Badge variant={getStatusVariant(suggestion.status)} className="flex items-center gap-1">
              {getStatusIcon(suggestion.status)}
              <span>{getStatusLabel(suggestion.status)}</span>
            </Badge>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
